/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceFilter.scss';

import {ChangeFunction, CloseButton, WithBackground} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {KeyboardEventHandler, MouseEventHandler} from 'react';
import {
    BolmateSelectItem, CompleteInvoiceFilter, CustomerType, InvoiceStatus, InvoiceType, OrderStatus
} from "../../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateSelect, DvrdDatePicker} from "../../controls";
import CountryField from "../../controls/country/field/countryField";
import classNames from 'classnames';

interface Props {
    onChangeFilter: (key: keyof CompleteInvoiceFilter) => ChangeFunction<string | null>;
    onSubmitFilters: MouseEventHandler & KeyboardEventHandler;
    onResetFilters: MouseEventHandler;
    onClose: MouseEventHandler;
    filters: CompleteInvoiceFilter;
    active: boolean;
}

const statusItems: Array<BolmateSelectItem> = [
    {label: 'Alle', value: ''},
    {label: 'Verzonden', value: InvoiceStatus.SENT},
    {label: 'Niet verzonden', value: InvoiceStatus.IMPORTED},
    {label: 'Wordt verzonden', value: InvoiceStatus.TRANSPORT},
    {label: 'Fout', value: InvoiceStatus.ERROR},
    {label: 'Waarschuwing', value: InvoiceStatus.WARNING},
];
const orderStatusItems: Array<BolmateSelectItem> = [
    {label: 'Alle', value: ''},
    {label: 'Geplaatst', value: OrderStatus.PLACED},
    {label: 'Verzonden', value: OrderStatus.SENT},
    {label: 'Geannuleerd', value: OrderStatus.CANCELED},
];
const invoiceTypeItems: Array<BolmateSelectItem> = [
    {label: 'Alle', value: ''},
    {label: 'Verkoopfacturen', value: InvoiceType.DEBET},
    {label: 'Creditfacturen', value: InvoiceType.CREDIT},
];
const customerTypeItems: Array<BolmateSelectItem> = [
    {label: 'Alle', value: CustomerType.BOTH},
    {label: 'Particulier', value: CustomerType.PRIVATE},
    {label: 'Zakelijk', value: CustomerType.BUSINESS},
]

export default function InvoiceFilter(props: Props) {
    const {onClose, active, filters, onChangeFilter, onResetFilters, onSubmitFilters} = props;

    function getLabelClassName(filterValue: string | null) {
        return filterValue ? 'bolmate-label-active' : undefined;
    }

    function fieldIsActive(filterValue: string | IDate | null | undefined): boolean {
        if (filterValue === null || filterValue === undefined) return false;
        else if (typeof filterValue === 'string' && !filterValue.length) return false;
        return true;
    }

    function _onChangeDate(key: keyof CompleteInvoiceFilter) {
        return function(value: IDate | null) {
            onChangeFilter(key)(value?.format('YYYY-MM-DD') ?? null);
        }
    }

    function _onClearDate(key: keyof CompleteInvoiceFilter) {
        return function() {
            _onChangeDate(key)(null);
        }
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <label className='row-label'>Factuur</label>
                <div className='row'>
                    <BolmateInput onChange={onChangeFilter('order_id')} value={filters.order_id ?? ''}
                                  label='Ordernummer' onEnter={onSubmitFilters}
                                  labelClassName={getLabelClassName(filters.order_id)}
                                  active={fieldIsActive(filters.order_id)}/>
                    <BolmateInput onChange={onChangeFilter('invoice_num')} value={filters.invoice_num ?? ''}
                                  label='Factuurnummer' onEnter={onSubmitFilters}
                                  labelClassName={getLabelClassName(filters.invoice_num)}
                                  active={fieldIsActive(filters.invoice_num)}/>
                </div>
                <div className='row'>
                    <BolmateSelect value={filters.invoice_type ?? ''} onChange={onChangeFilter('invoice_type')}
                                   items={invoiceTypeItems} label='Soort factuur'
                                   active={fieldIsActive(filters.invoice_type)}
                                   labelClassName={getLabelClassName(filters.invoice_type)}/>
                    <BolmateSelect value={filters.status ?? ''} onChange={onChangeFilter('status')} items={statusItems}
                                   label='Status factuur' active={fieldIsActive(filters.status)}
                                   labelClassName={getLabelClassName(filters.status)}/>
                </div>
                <div className='row'>
                    <BolmateInput onChange={onChangeFilter('reference')} value={filters.reference ?? ''}
                                  label='Referentie' labelClassName={getLabelClassName(filters.reference)}
                                  active={fieldIsActive(filters.reference)} onEnter={onSubmitFilters}/>
                </div>
                <div className='row'>
                    {renderDatePicker('date_from', 'Orders geplaatst vanaf')}
                    {renderDatePicker('date_to', 'Orders geplaatst t/m')}
                </div>
                <label className='row-label'>Klant</label>
                <div className='row'>
                    {renderCustomerType()}
                </div>
                <div className='row'>
                    <BolmateInput onChange={onChangeFilter('firstname')} value={filters.firstname ?? ''}
                                  label='Voornaam' labelClassName={getLabelClassName(filters.firstname)}
                                  active={fieldIsActive(filters.firstname)} onEnter={onSubmitFilters}/>
                    <BolmateInput onChange={onChangeFilter('lastname')} value={filters.lastname ?? ''}
                                  label='Achternaam' labelClassName={getLabelClassName(filters.lastname)}
                                  active={fieldIsActive(filters.lastname)} onEnter={onSubmitFilters}/>
                </div>
                <div className='row'>
                    <CountryField onChange={onChangeFilter('country')} label='Land'
                                  value={filters.country ?? undefined}
                                  labelClassName={getLabelClassName(filters.country)} labelAsValue
                                  active={fieldIsActive(filters.country)} itemsPosition='top'/>
                    <BolmateInput onChange={onChangeFilter('company')} value={filters.company ?? ''}
                                  label='Bedrijfsnaam' labelClassName={getLabelClassName(filters.company)}
                                  active={fieldIsActive(filters.company)} onEnter={onSubmitFilters}/>
                </div>
                <div className='row'>
                    <BolmateInput onChange={onChangeFilter('vat_number')} value={filters.vat_number ?? ''}
                                  label='BTW nummer' labelClassName={getLabelClassName(filters.vat_number)}
                                  active={fieldIsActive(filters.vat_number)} onEnter={onSubmitFilters}/>
                    <BolmateInput onChange={onChangeFilter('coc_number')} value={filters.coc_number ?? ''}
                                  label='KvK nummer' labelClassName={getLabelClassName(filters.coc_number)}
                                  active={fieldIsActive(filters.coc_number)} onEnter={onSubmitFilters}/>
                </div>
                <label className='row-label'>Bestelling</label>
                <div className='row'>
                    <BolmateSelect value={filters.order_status ?? ''} onChange={onChangeFilter('order_status')}
                                   items={orderStatusItems} label='Status bestelling' itemsPosition='top'
                                   labelClassName={getLabelClassName(filters.order_status)}
                                   active={fieldIsActive(filters.order_status)}/>
                    <BolmateInput onChange={onChangeFilter('ean')} value={filters.ean ?? ''}
                                  label='EAN besteld product' className={getLabelClassName(filters.ean)}
                                  active={fieldIsActive(filters.ean)} onEnter={onSubmitFilters} placeholder='EAN'/>
                </div>
            </div>
        )
    }

    function renderDatePicker(key: keyof CompleteInvoiceFilter, label: string) {
        const filterValue = filters[key];
        const value: IDate | null = !!filterValue ? new IDate(filterValue) : null;
        const isActive = fieldIsActive(filterValue);
        const max = IDate.now().maxTime();
        return (
            <div className='date-filter-container'>
                <DvrdDatePicker onChange={_onChangeDate(key)} value={value} label={label} dateFormat='DD-MM-YYYY'
                                placeholder='dd-mm-jjjj' className={classNames('date-filter', isActive && 'active')}
                                max={max} closeOnChange/>
                {!!value && <CloseButton onClick={_onClearDate(key)} className='clear-btn'/>}
            </div>
        )
    }

    function renderCustomerType() {
        const isActive = filters.customer_type !== CustomerType.BOTH
        return (
            <BolmateSelect value={filters.customer_type} onChange={onChangeFilter('customer_type')}
                           items={customerTypeItems} label='Type klant' active={isActive}
                           labelClassName={classNames(isActive && 'bolmate-label-active')}/>
        )
    }

    return (
        <WithBackground active={active} onClose={onClose} backgroundColor='transparent'>
            <div className='invoice-filter default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Uitgebreid zoeken</label>
                    <CloseButton onClick={onClose}/>
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <BolmateButton onClick={onResetFilters} label='Filters wissen' secondary/>
                    <BolmateButton onClick={onSubmitFilters} label='Zoeken'/>
                </div>
            </div>
        </WithBackground>
    )
}
